import type { Ref } from 'vue'
import type { OrderPerson } from '@/composables/useMultipleDiners'

interface ItemGroup {
  menu_item_id: number
  variant_id?: number | null
  special_instructions?: string | null
  price?: number
}

interface UsePersonItemsEditorOptions {
  persons: Ref<OrderPerson[]>
  activePersonIndex: Ref<number>
  removePerson: (index: number) => boolean
  removeItemFromPerson: (personIndex: number, itemIndex: number) => void
  confirmRemovePerson: () => boolean
}

export function usePersonItemsEditor(options: UsePersonItemsEditorOptions) {
  const removePersonWithConfirm = (index: number) => {
    if (options.confirmRemovePerson()) {
      options.removePerson(index)
    }
  }

  const removeItemFromPerson = (personIndex: number, itemIndex: number) => {
    options.removeItemFromPerson(personIndex, itemIndex)
  }

  const removeGroupFromPerson = (group: ItemGroup) => {
    if (!group || typeof group.menu_item_id === 'undefined') {
      console.error('Invalid group data in removeGroupFromPerson:', group)
      return
    }

    const person = options.persons.value[options.activePersonIndex.value]
    if (!person) return

    person.items = person.items.filter((item) => {
      const itemMatches =
        item.menu_item_id === group.menu_item_id &&
        (item.variant_id || null) === (group.variant_id || null) &&
        (item.special_instructions || null) === (group.special_instructions || null)

      return !itemMatches
    })
  }

  const updateItemQuantity = (data: { group: ItemGroup; newQuantity: number }) => {
    console.log('updateItemQuantity called with:', data)
    const { group, newQuantity } = data

    if (!group || typeof group.menu_item_id === 'undefined') {
      console.error('Invalid group data in updateItemQuantity:', group)
      console.error('Full data received:', data)
      return
    }

    const person = options.persons.value[options.activePersonIndex.value]
    if (!person) return

    if (newQuantity <= 0) {
      removeGroupFromPerson(group)
      return
    }

    const matchingItems = person.items.filter(
      (item) =>
        item.menu_item_id === group.menu_item_id &&
        (item.variant_id || null) === (group.variant_id || null) &&
        (item.special_instructions || null) === (group.special_instructions || null)
    )

    const currentQuantity = matchingItems.length
    const quantityDifference = newQuantity - currentQuantity

    if (quantityDifference > 0) {
      const templateItem = matchingItems[0] || {
        menu_item_id: group.menu_item_id,
        variant_id: group.variant_id || null,
        quantity: 1,
        special_instructions: group.special_instructions || null,
        unit_price: group.price,
        person_id: person.id || null
      }

      for (let i = 0; i < quantityDifference; i++) {
        person.items.push({ ...templateItem })
      }
    } else if (quantityDifference < 0) {
      const itemsToRemove = Math.abs(quantityDifference)

      for (let i = 0; i < itemsToRemove && matchingItems.length > 0; i++) {
        const itemToRemove = matchingItems.pop()
        const index = person.items.findIndex(
          (item) =>
            item.menu_item_id === itemToRemove?.menu_item_id &&
            (item.variant_id || null) === (itemToRemove?.variant_id || null) &&
            (item.special_instructions || null) === (itemToRemove?.special_instructions || null)
        )

        if (index !== -1) {
          person.items.splice(index, 1)
        }
      }
    }
  }

  return {
    removePersonWithConfirm,
    removeItemFromPerson,
    removeGroupFromPerson,
    updateItemQuantity
  }
}
